import type { ModalProps } from '@/components/common/ModalDialogV2.vue';
import { reactive, readonly, markRaw } from 'vue';
import type { Component } from 'vue';

export interface ModalOptions extends ModalProps {
  content?: string;
  customComponent?: Component;
  customComponentProps?: Record<string, any>;
  slots?: {
    headerSlot?: Component;
    headerProps?: Record<string, any>;
    iconSlot?: Component;
    iconProps?: Record<string, any>;
    descriptionSlot?: Component;
    descriptionProps?: Record<string, any>;
    footerSlot?: Component;
    footerProps?: Record<string, any>;
    confirmButtonSlot?: Component;
    confirmButtonProps?: Record<string, any>;
    cancelButtonSlot?: Component;
    cancelButtonProps?: Record<string, any>;
  };
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  teleport?: string;
}

interface Modal extends ModalOptions {
  id: string;
  isOpen: boolean;
}

// Create a single instance to be shared across the app
const modals = reactive<Modal[]>([]);

// Use a counter to generate unique IDs
let modalCounter = 0;

/**
 * Composable for programmatically managing modals
 */
export function useModal() {
  const { t } = useTranslations();

  /**
   * Create and open a new modal
   * @param options Modal configuration options
   * @returns Modal ID and control functions
   */
  function create(options: ModalOptions = {}) {
    const id = `modal-${modalCounter++}`;

    // Apply markRaw to any component to prevent Vue from making them reactive
    const processedOptions = { ...options };

    if (processedOptions.customComponent) {
      processedOptions.customComponent = markRaw(processedOptions.customComponent);
    }
    if (processedOptions.slots?.headerSlot) {
      processedOptions.slots.headerSlot = markRaw(processedOptions.slots.headerSlot);
    }
    if (processedOptions.slots?.iconSlot) {
      processedOptions.slots.iconSlot = markRaw(processedOptions.slots.iconSlot);
    }
    if (processedOptions.slots?.descriptionSlot) {
      processedOptions.slots.descriptionSlot = markRaw(processedOptions.slots.descriptionSlot);
    }
    if (processedOptions.slots?.footerSlot) {
      processedOptions.slots.footerSlot = markRaw(processedOptions.slots.footerSlot);
    }
    if (processedOptions.slots?.confirmButtonSlot) {
      processedOptions.slots.confirmButtonSlot = markRaw(processedOptions.slots.confirmButtonSlot);
    }
    if (processedOptions.slots?.cancelButtonSlot) {
      processedOptions.slots.cancelButtonSlot = markRaw(processedOptions.slots.cancelButtonSlot);
    }

    const modal: Modal = {
      id,
      isOpen: true,
      ...processedOptions,
    };

    modals.push(modal);

    return {
      id,
      close: () => close(id),
      update: (newOptions: Partial<ModalOptions>) => update(id, newOptions),
    };
  }

  /**
   * Close and remove a modal by ID
   * @param id Modal ID
   */
  function close(id: string) {
    const index = modals.findIndex((modal) => modal.id === id);
    if (index !== -1) {
      modals[index].isOpen = false;
      // Remove the modal after animation completes
      setTimeout(() => {
        const currentIndex = modals.findIndex((modal) => modal.id === id);
        if (currentIndex !== -1) {
          modals.splice(currentIndex, 1);
        }
      }, 300);
    }
  }

  /**
   * Update an existing modal's options
   * @param id Modal ID
   * @param options New modal options
   */
  function update(id: string, options: Partial<ModalOptions>) {
    const index = modals.findIndex((modal) => modal.id === id);
    if (index !== -1) {
      // Process component props with markRaw
      const processedOptions = { ...options };

      if (processedOptions.customComponent) {
        processedOptions.customComponent = markRaw(processedOptions.customComponent);
      }
      if (processedOptions.slots?.headerSlot) {
        processedOptions.slots.headerSlot = markRaw(processedOptions.slots.headerSlot);
      }
      if (processedOptions.slots?.iconSlot) {
        processedOptions.slots.iconSlot = markRaw(processedOptions.slots.iconSlot);
      }
      if (processedOptions.slots?.descriptionSlot) {
        processedOptions.slots.descriptionSlot = markRaw(processedOptions.slots.descriptionSlot);
      }
      if (processedOptions.slots?.footerSlot) {
        processedOptions.slots.footerSlot = markRaw(processedOptions.slots.footerSlot);
      }
      if (processedOptions.slots?.confirmButtonSlot) {
        processedOptions.slots.confirmButtonSlot = markRaw(
          processedOptions.slots.confirmButtonSlot,
        );
      }
      if (processedOptions.slots?.cancelButtonSlot) {
        processedOptions.slots.cancelButtonSlot = markRaw(processedOptions.slots.cancelButtonSlot);
      }

      Object.assign(modals[index], processedOptions);
    }
  }

  /**
   * Close all open modals
   */
  function closeAllModals() {
    modals.forEach((modal) => {
      modal.isOpen = false;
    });
    // Remove all modals after animation completes
    setTimeout(() => {
      modals.splice(0, modals.length);
    }, 300);
  }

  /**
   * Create a confirmation modal with predefined options
   * @param options Modal configuration options
   * @returns Modal ID and control functions
   */
  function confirm(options: ModalOptions & { message?: string } = {}) {
    return create({
      type: 'info',
      icon: 'heroicons:question-mark-circle',
      title: options.title || t('common.confirm'),
      description: options.message || options.description || t('common.are_you_sure'),
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: options.confirmButtonText || t('common.confirm'),
      cancelButtonText: options.cancelButtonText || t('common.cancel'),
      ...options,
    });
  }

  /**
   * Create an alert modal with predefined options
   * @param options Modal configuration options
   * @returns Modal ID and control functions
   */
  function alert(options: ModalOptions & { message?: string } = {}) {
    return create({
      type: options.type || 'info',
      showCancelButton: false,
      confirmButtonText: options.confirmButtonText || t('common.close'),
      title: options.title || t('common.attention'),
      description: options.message || options.description || '',
      ...options,
    });
  }

  /**
   * Create a success modal with predefined options
   * @param options Modal configuration options
   * @returns Modal ID and control functions
   */
  function success(options: ModalOptions & { message?: string } = {}) {
    return alert({
      type: 'success',
      title: options.title || t('common.success'),
      ...options,
    });
  }

  /**
   * Create an error modal with predefined options
   * @param options Modal configuration options
   * @returns Modal ID and control functions
   */
  function error(options: ModalOptions & { message?: string } = {}) {
    return alert({
      type: 'error',
      title: options.title || t('common.error'),
      ...options,
    });
  }

  return {
    modals: readonly(modals),
    create,
    close,
    update,
    closeAllModals,
    confirm,
    alert,
    success,
    error,
  };
}
